import { useCallback, useRef, useState } from 'react';

/**
 * Wraps an async action so a second call while the first is still running
 * does nothing. The ref is what refuses it: a click landing before React
 * re-renders still sees the old `pending`. Released however the action ends,
 * so a failure can be retried (PSTUDIO-4634).
 */
export const useSingleFlight = (
  run: () => Promise<void>
): [start: () => Promise<void>, pending: boolean] => {
  const busy = useRef(false);
  const latest = useRef(run);
  latest.current = run;
  const [pending, setPending] = useState(false);
  const start = useCallback(async () => {
    if (busy.current) return;
    busy.current = true;
    setPending(true);
    try {
      await latest.current();
    } finally {
      busy.current = false;
      setPending(false);
    }
  }, []);
  return [start, pending];
};
